// Caja «Asistencia y recursos del viaje» bajo el botón de reserva (V2).
import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { Download, Headset, Mail, MessageCircle, Phone, Sparkles } from "lucide-react";
import { SITE } from "@/lib/site";

type L = "es" | "en" | "fr" | "pt" | "it" | "de";
const TXT: Record<L, { title: string; wa: string; care: string; guide: string; maya: string; msg: string; hours: string; form: string }> = {
  es: { title: "Asistencia y recursos del viaje", wa: "WhatsApp", care: "Atención a clientes", guide: "Guía descargable", maya: "Conocer a Maya", msg: "Hola, tengo preguntas sobre el tour", hours: "Atención al cliente 24/7", form: "Formulario de contacto" },
  en: { title: "Trip help & resources", wa: "WhatsApp", care: "Customer care", guide: "Downloadable guide", maya: "Meet Maya", msg: "Hi, I have questions about the tour", hours: "Customer care 24/7", form: "Contact form" },
  fr: { title: "Assistance et ressources", wa: "WhatsApp", care: "Service client", guide: "Guide à télécharger", maya: "Rencontrer Maya", msg: "Bonjour, j'ai des questions sur le tour", hours: "Service client 24h/24, 7j/7", form: "Formulaire de contact" },
  pt: { title: "Ajuda e recursos da viagem", wa: "WhatsApp", care: "Atendimento ao cliente", guide: "Guia para baixar", maya: "Conhecer a Maya", msg: "Olá, tenho perguntas sobre o tour", hours: "Atendimento 24/7", form: "Formulário de contato" },
  it: { title: "Assistenza e risorse del viaggio", wa: "WhatsApp", care: "Assistenza clienti", guide: "Guida da scaricare", maya: "Conosci Maya", msg: "Ciao, ho domande sul tour", hours: "Assistenza clienti 24/7", form: "Modulo di contatto" },
  de: { title: "Hilfe & Reiseinfos", wa: "WhatsApp", care: "Kundenservice", guide: "Reiseführer (PDF)", maya: "Maya kennenlernen", msg: "Hallo, ich habe Fragen zur Tour", hours: "Kundenservice 24/7", form: "Kontaktformular" },
};


interface Props {
  lang: string;
  tourTitle: string;
  share?: { slug: string; priceFrom: number | null; currency: string | null; image: string | null };
  onMaya?: () => void;
}

export function TourV2HelpBox({ lang, tourTitle, onMaya, share }: Props) {
  const k = (lang || "es").slice(0, 2) as L;
  const tx = TXT[k] ?? TXT.es;
  const [care, setCare] = useState(false);
  const wa = `${SITE.contact.whatsappUrl}?text=${encodeURIComponent(`${tx.msg} «${tourTitle}»`)}`;
  const btn =
    "flex min-h-[56px] flex-col items-center justify-center gap-1 rounded-xl border border-border bg-background px-2 py-2 text-center text-xs font-semibold leading-tight sm:min-h-0 sm:flex-row sm:gap-2 sm:px-3 sm:py-2.5 sm:text-sm transition hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary";

  return (
    <section aria-label={tx.title} className="rounded-2xl border border-border bg-card p-3 shadow-sm sm:p-4" data-testid="v2-help-box">
      <h2 className="mb-2.5 text-sm font-bold sm:mb-3">{tx.title}</h2>
      <div className="grid grid-cols-2 gap-2">
        <a href={wa} target="_blank" rel="noopener noreferrer" className={btn}>
          <MessageCircle className="h-5 w-5 shrink-0 text-primary sm:h-4 sm:w-4" aria-hidden />
          {tx.wa}
        </a>
        <button type="button" onClick={() => setCare((v) => !v)} aria-expanded={care} className={btn}>
          <Headset className="h-5 w-5 shrink-0 text-primary sm:h-4 sm:w-4" aria-hidden />
          {tx.care}
        </button>
        <button
          type="button"
          onClick={() => document.getElementById("v2-guia")?.scrollIntoView({ behavior: "smooth", block: "start" })}
          className={btn}
        >
          <Download className="h-5 w-5 shrink-0 text-primary sm:h-4 sm:w-4" aria-hidden />
          {tx.guide}
        </button>
        {onMaya && (
          <button type="button" onClick={onMaya} className={btn}>
            <Sparkles className="h-5 w-5 shrink-0 text-primary sm:h-4 sm:w-4" aria-hidden />
            {tx.maya}
          </button>
        )}
      </div>
      {care && (
        <div className="mt-3 space-y-1.5 rounded-xl bg-muted/50 p-3 text-sm">
          <p className="font-semibold">{tx.hours}</p>
          <a href={`tel:${SITE.contact.phoneTel}`} className="flex min-h-[44px] items-center gap-2 hover:text-primary">
            <Phone className="h-4 w-4" aria-hidden /> {SITE.contact.phone}
          </a>
          <a href={`mailto:${SITE.contact.email}`} className="flex min-h-[44px] items-center gap-2 hover:text-primary">
            <Mail className="h-4 w-4" aria-hidden /> {SITE.contact.email}
          </a>
          <Link to="/contact" className="block text-primary underline-offset-2 hover:underline">
            {tx.form}
          </Link>
        </div>
      )}
    </section>
  );
}
